import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_useBlocker as useBlocker } from 'react-router-dom'

import { F0ActionBar, F0ActionBarRef } from '@factorialco/f0-react/dist/experimental'
import { ActionBarStatus } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { QuestionnaireType, ValidationResult } from './types'

function defaultValidation(questionnaire: QuestionnaireType): ValidationResult {
  const hasEmptyTitles = questionnaire.elements.some((element) => {
    if (!element.label.trim()) return true
    if (element.type === 'section') {
      return element.elements.some((question) => !question.label.trim())
    }
    return false
  })

  if (hasEmptyTitles) {
    return { ok: false, message: i18n.t('forms.action_bar.empty_title_error') }
  }

  return { ok: true }
}

interface ActionBarState {
  label: string
  status?: ActionBarStatus
}

function getDefaultState(): ActionBarState {
  return { label: i18n.t('forms.action_bar.label') }
}

interface FormEditorActionsProps {
  isDirty: boolean
  questionnaire: QuestionnaireType
  onSave: () => Promise<boolean>
  onDiscard: () => void
  isThinking?: boolean
  validate?: (questionnaire: QuestionnaireType) => ValidationResult
}

export const FormEditorActions = ({
  isDirty,
  questionnaire,
  onSave,
  onDiscard,
  isThinking,
  validate,
}: FormEditorActionsProps) => {
  const actionBarRef = useRef<F0ActionBarRef>(null)
  const [barState, setBarState] = useState<ActionBarState>(getDefaultState)

  const blocker = useBlocker(isDirty)

  const validation = useMemo(
    () => (validate ?? defaultValidation)(questionnaire),
    [questionnaire, validate]
  )

  useEffect(() => {
    if (barState.status === 'error' && validation.ok) {
      setBarState(getDefaultState())
    }
  }, [validation, barState.status])

  const handleSave = useCallback(async () => {
    if (!validation.ok) {
      setBarState({ label: validation.message, status: 'error' })
      actionBarRef.current?.wiggle({ errorHighlight: true })
      return
    }
    setBarState({ label: i18n.t('forms.action_bar.label'), status: 'loading' })
    const success = await onSave()
    if (success) {
      setBarState({ label: i18n.t('forms.action_bar.saved'), status: 'success' })
      setTimeout(() => setBarState(getDefaultState()), 1500)
    } else {
      setBarState(getDefaultState())
    }
  }, [validation, onSave])

  const handleDiscard = useCallback(() => {
    setBarState(getDefaultState())
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (blocker.state === 'blocked') {
      actionBarRef.current?.wiggle({ errorHighlight: true })
      blocker.reset?.()
    }
  }, [blocker])

  return (
    <F0ActionBar
      ref={actionBarRef}
      isOpen={isDirty || barState.status === 'success'}
      variant="light"
      status={barState.status}
      primaryActions={[
        { label: i18n.t('forms.action_bar.save'), onClick: handleSave, disabled: isThinking },
      ]}
      secondaryActions={[
        { label: i18n.t('forms.action_bar.discard'), onClick: handleDiscard, disabled: isThinking },
      ]}
      label={barState.label}
    />
  )
}
